/**
 * Storage Service - Handle localStorage operations with error handling
 */

import logger from '../core/logger.js';
import { StorageError } from '../errors/app-errors.js';
import { captureException } from '../monitoring/sentry.js';
import { showToast } from '../ui/toast.js';

/**
 * Check if an error is a quota exceeded error
 */
function isQuotaExceededError(e: unknown): boolean {
  if (e && typeof e === 'object' && 'name' in e) {
    const error = e as { name: string; code?: number };
    return error.name === 'QuotaExceededError' || error.code === 22 || error.code === 1014;
  }
  return false;
}

/**
 * Handle quota exceeded errors
 */
function handleQuotaError(key: string, operation: string, originalError: Error): void {
  const error = new StorageError(
    'Storage quota exceeded',
    key,
    operation
  );
  
  captureException(error, {
    service: 'StorageService',
    method: operation,
    key
  });
  
  showToast('Storage is full. Please clear some data.', 'error', 5000);
  
  logger.error('Storage quota exceeded', { key, error: originalError.message });
}

/**
 * Safely get an item from localStorage
 */
export function getItem(key: string, defaultValue: string | null = null): string | null {
  try {
    const value = localStorage.getItem(key);
    return value !== null ? value : defaultValue;
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : String(e);
    logger.warn(`localStorage.getItem failed for key: ${key}`, { error: errorMessage });
    return defaultValue;
  }
}

/**
 * Safely set an item in localStorage
 */
export function setItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    if (isQuotaExceededError(e)) {
      handleQuotaError(key, 'setItem', e as Error);
      return false;
    }
    
    const errorMessage = e instanceof Error ? e.message : String(e);
    logger.warn(`localStorage.setItem failed for key: ${key}`, { error: errorMessage });
    return false;
  }
}

/**
 * Safely remove an item from localStorage
 */
export function removeItem(key: string): boolean {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : String(e);
    logger.warn(`localStorage.removeItem failed for key: ${key}`, { error: errorMessage });
    return false;
  }
}

/**
 * Safely get a JSON object from localStorage
 */
export function getJSON<T>(key: string, defaultValue: T | null = null): T | null {
  try {
    const value = localStorage.getItem(key);
    if (value === null) {
      return defaultValue;
    }
    return JSON.parse(value) as T;
  } catch (e) {
    // If JSON parsing fails, the data is corrupted
    if (e instanceof SyntaxError) {
      const error = new StorageError(
        'Corrupted data in localStorage',
        key,
        'getJSON'
      );
      
      captureException(error, {
        service: 'StorageService',
        method: 'getJSON',
        key
      });
      
      logger.warn(`localStorage data corrupted for key: ${key}, removing`, { error: e.message });
      
      // Automatically remove corrupted data
      try {
        localStorage.removeItem(key);
      } catch (removeError) {
        // Ignore removal errors
      }
      
      return defaultValue;
    }
    
    const errorMessage = e instanceof Error ? e.message : String(e);
    logger.warn(`localStorage.getJSON failed for key: ${key}`, { error: errorMessage });
    return defaultValue;
  }
}

/**
 * Safely set a JSON object in localStorage
 */
export function setJSON<T>(key: string, value: T): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (e) {
    if (isQuotaExceededError(e)) {
      handleQuotaError(key, 'setJSON', e as Error);
      return false;
    }
    
    const errorMessage = e instanceof Error ? e.message : String(e);
    logger.warn(`localStorage.setJSON failed for key: ${key}`, { error: errorMessage });
    return false;
  }
}

/**
 * Check if localStorage is available
 */
export function isAvailable(): boolean {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, '1');
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Clear all localStorage items
 */
export function clear(): boolean {
  try {
    localStorage.clear();
    return true;
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : String(e);
    logger.warn('localStorage.clear failed', { error: errorMessage });
    return false;
  }
}

export default {
  getItem,
  setItem,
  removeItem,
  getJSON,
  setJSON,
  isAvailable,
  clear
};
